import { useState, type ReactNode } from "react";
import { ChevronDown, ShieldAlert } from "lucide-react";
import { cn } from "../../lib/cn";
import { microcopy, useDocumentLocale } from "../../lib/microcopy";
import { CitationHoverCard, citationIcon, citationKind, type CitationLike } from "../citations";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../collapsible";

/** The sources an answer drew on, folded into one line ("Used 6 sources") that opens to a card per source. */
export function Sources({ citations, onOpen, label, defaultOpen = false, className }: {
  citations: CitationLike[];
  onOpen?: (c: CitationLike) => void;
  label?: ReactNode;
  defaultOpen?: boolean;
  className?: string;
}) {
  const locale = useDocumentLocale();
  const [open, setOpen] = useState(defaultOpen);
  if (citations.length === 0) return null;
  const kinds = Array.from(new Set(citations.map((c) => c.sourceType))).slice(0, 3);
  const unverified = citations.some((c) => c.supported === false);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className={cn("mt-3", className)}>
      <CollapsibleTrigger className="group/sources inline-flex items-center gap-2 rounded-full border border-sand-200 bg-white py-1 pe-2.5 ps-1 text-xs font-medium text-sand-800 transition hover:border-aegold-300 hover:text-aeblack-900">
        <span className="flex -space-x-1">
          {kinds.map((k) => {
            const Icon = citationIcon(k);
            return <span key={k} className="grid size-5 place-items-center rounded-full bg-aegold-50 text-aegold-700 ring-2 ring-white"><Icon className="size-3" /></span>;
          })}
        </span>
        {label ?? (citations.length === 1 ? microcopy("usedOneSource", locale) : microcopy("usedSources", locale, { count: citations.length }))}
        {unverified ? <ShieldAlert className="size-3.5 text-danger-600" /> : null}
        <ChevronDown className="size-3.5 transition-transform group-data-[state=open]/sources:rotate-180" />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul className="mt-2 grid gap-1.5 sm:grid-cols-2">
          {citations.map((c, i) => {
            const Icon = citationIcon(c.sourceType);
            return (
              <li key={c.id} className="min-w-0">
                <CitationHoverCard citation={c} openable={!!onOpen}>
                  <button
                    type="button"
                    onClick={() => onOpen?.(c)}
                    className={cn("flex w-full items-start gap-2 rounded-lg border bg-white p-2 text-start transition hover:border-aegold-300 hover:bg-aegold-50/40", c.supported === false ? "border-danger-200" : "border-sand-200")}
                  >
                    <span className="grid size-5 shrink-0 place-items-center rounded-full bg-aegold-600 text-[10px] font-semibold text-white">{c.index ?? i + 1}</span>
                    <span className="min-w-0 flex-1">
                      <span className="line-clamp-2 block text-xs font-medium leading-snug text-aeblack-900" dir="auto">{c.title}</span>
                      <span className="mt-0.5 flex min-w-0 items-center gap-1 text-[11px] text-muted-foreground">
                        <Icon className="size-3 shrink-0" /> <span className="shrink-0">{citationKind(c.sourceType, locale)}</span>
                        {c.locator ? <span className="truncate" dir="auto">· {c.locator}</span> : null}
                      </span>
                    </span>
                  </button>
                </CitationHoverCard>
              </li>
            );
          })}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
}
